import { useState, useRef, useMemo, useCallback, useEffect } from 'react'
import { Menu } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { NavHeaderLinkItem, NavLinkItemValue } from '@/gql/graphql'
import { MEGA_MENU_PT, MEGA_MENU_PT_HORIZONTAL, resolveNavLink } from '@/utils/navigation'
import { MegaMenu } from 'primereact/megamenu'
import { MenuItem } from 'primereact/menuitem'
import MobileNav from './MobileNav'

type NavItem = Pick<NavHeaderLinkItem, 'link' | 'children' | 'label'>

export interface NavProps {
  menuItems: NavItem[]
  horizontalNavItems: NavItem[]
  isNavOpen: boolean
  onNavClick: (url: string, isExternal: boolean) => void
  setIsNavOpen: (open: boolean) => void
  className?: string
}

export interface CustomMenuItem extends MenuItem {
  openInNewTab?: boolean
  items?: CustomMenuItem[] | CustomMenuItem[][]
}

function buildMegaMenuItems(
  items: NavItem[],
  onNavigate: (url: string, isExternal: boolean) => void,
  options?: { navigateParentsWithChildren?: boolean }
): CustomMenuItem[] {
  const navigateParentsWithChildren = options?.navigateParentsWithChildren ?? true

  return items.map((item: NavItem) => {
    const childrenItems: CustomMenuItem[] =
      item.children?.map((child: NavLinkItemValue) => ({
        label: child.label,
        url: child.url || '#',
        openInNewTab: child.openInNewTab,
        command: () => {
          const { url, isExternal } = resolveNavLink(child)
          onNavigate(url, isExternal)
        },
      })) || []

    const menuItem: CustomMenuItem = {
      label: item.label,
    }

    if (childrenItems.length > 0) {
      menuItem.items = [
        [
          {
            items: childrenItems,
          },
        ],
      ]

      if (navigateParentsWithChildren) {
        menuItem.url = item.link?.url || '#'
        menuItem.command = () => {
          const { url, isExternal } = resolveNavLink(item.link)
          onNavigate(url, isExternal)
        }
      }
    } else {
      menuItem.url = item.link?.url || '#'
      menuItem.command = () => {
        const { url, isExternal } = resolveNavLink(item.link)
        onNavigate(url, isExternal)
      }
    }

    return menuItem
  })
}

export default function Nav(props: NavProps) {
  const {
    menuItems,
    horizontalNavItems,
    isNavOpen,
    setIsNavOpen,
    className,
  } = props
  const { t } = useTranslation()
  const [isMenuDropdownOpen, setIsMenuDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  const handleMenuNavigate = useCallback(
    (url: string, isExternal: boolean) => {
      if (isExternal) {
        window.open(url, '_blank')
      } else {
        setIsMenuDropdownOpen(false)
        setIsNavOpen(false)
        navigate(url)
      }
    },
    [navigate, setIsNavOpen]
  )

  const megaMenuItems = useMemo(
    () => buildMegaMenuItems(menuItems, handleMenuNavigate),
    [menuItems, handleMenuNavigate]
  )

  const horizontalMegaMenuItems = useMemo(
    () => buildMegaMenuItems(horizontalNavItems, handleMenuNavigate),
    [horizontalNavItems, handleMenuNavigate]
  )

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsMenuDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  return (
    <>
      {/* Desktop navigation */}
      <nav className={`hidden md:block border-t border-secondary/10 pt-2 ${className || ''}`}>
        <div className="flex flex-wrap items-center gap-6 text-sm text-foreground">
          <div className="relative shrink-0" ref={dropdownRef}>
            <button
              type="button"
              onClick={() => setIsMenuDropdownOpen(!isMenuDropdownOpen)}
              aria-expanded={isMenuDropdownOpen}
              aria-haspopup="menu"
              className={`flex items-center gap-2 transition-colors cursor-pointer ${isMenuDropdownOpen ? 'text-primary-text' : 'text-primary-text hover:text-secondary'}`}
            >
              <Menu aria-hidden="true" className="w-5 h-5" />
              {t('nav.menu')}
            </button>

            {isMenuDropdownOpen && (
              <div className="absolute top-full left-0 mt-3 w-64 bg-background border border-secondary/10 shadow-lg z-50 rounded-b-md">
                <MegaMenu
                  orientation="vertical"
                  model={megaMenuItems}
                  pt={MEGA_MENU_PT}
                  unstyled
                />
              </div>
            )}
          </div>

          {horizontalMegaMenuItems.length > 0 && (
            <MegaMenu
              orientation="horizontal"
              model={horizontalMegaMenuItems}
              pt={MEGA_MENU_PT_HORIZONTAL}
              unstyled
            />
          )}
        </div>
      </nav>

      {/* Mobile navigation (collapsible) */}
      {isNavOpen && (
        <div className="flex flex-col gap-6 md:hidden">
          <div className="flex flex-col">
            <h2 className="text-sm font-bold text-foreground/80">{t('nav.mainMenu')}</h2>
            <nav className={`md:hidden pt-3 border-secondary/10 ${className || ''}`}>
              <MobileNav items={menuItems} onNavigate={handleMenuNavigate} />
            </nav>
          </div>

          <div className="flex flex-col md:hidden">
            <h2 className="text-sm font-bold text-foreground/80">{t('nav.usefulLinks')}</h2>
            <nav className={`md:hidden pt-3 border-secondary/10 ${className || ''}`}>
              <MobileNav items={horizontalNavItems} onNavigate={handleMenuNavigate} />
            </nav>
          </div>
        </div>
      )}
    </>
  )
}
